import { useMemo, useState } from 'react';

import { canUpdateTaskFile, isImageFile, TaskFile } from 'entities';

import {
  Button,
  Card,
  Column,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTitle,
  Input,
  Row,
  Tooltip,
} from 'components/Blocks';
import { DownloadFileButton } from 'components/Buttons';
import { UserAvatar } from 'components/UserAvatar';
import { TruncateText } from 'components/TruncateText';
import { ChevronDownIcon, FileUpIcon } from 'components/Icons';
import { PrivateImage } from 'components/Uploads';

import { useTaskModal } from '../../context.taskModal';
import { FileVersionTag } from '../../../FileHistoryTag';
import { RenameTaskFileButton } from './RenameTaskFileButton';
import { NewTaskFileVersionButton } from './NewFileVersion';

import { useCommon } from 'hooks/common';

import { fixFileName, getFirstAndLastName, isSubstring } from 'utils/text';
import { dateDistanceToNow, formatPublishedAtDate } from 'utils/date';
import { listGet, sortByProperty } from 'utils/array';

const allTypes = {
  value: 'all',
  checker: () => true,
};

const specificTypes: Array<{
  value: string;
  checker: (f: TaskFile) => boolean;
}> = [
  {
    value: 'image',
    checker: isImageFile,
  },
  {
    value: 'video',
    checker: (f) => !!f.contentType?.startsWith('video'),
  },
  {
    value: 'audio',
    checker: (f) => !!f.contentType?.startsWith('audio'),
  },
  {
    value: 'PDF',
    checker: (f) => f.contentType === 'application/pdf',
  },
  {
    value: 'Microsoft Office',
    checker: (f) =>
      [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'application/vnd.ms-powerpoint',
      ].includes(f.contentType || ''),
  },
  {
    value: 'PPT',
    checker: (f) => f.contentType === 'application/vnd.ms-powerpoint',
  },
  {
    value: 'Excel',
    checker: (f) =>
      [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ].includes(f.contentType || ''),
  },
  {
    value: 'Word',
    checker: (f) =>
      [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
      ].includes(f.contentType || ''),
  },
];

const unknownTypes = {
  value: 'unknown',
  checker: (f: TaskFile) => specificTypes.every((t) => !t.checker(f)),
};

const types = [allTypes, ...specificTypes, unknownTypes];

export function TasksFiles() {
  const { t, user } = useCommon();

  const { task, project } = useTaskModal();

  const [selectedType, setState] = useState(allTypes.value);
  const [query, setQuery] = useState('');

  const actualFiles = useMemo(() => {
    return sortByProperty(task.files, 'uploadedAt', { uploadedAt: 'DESC' });
  }, [task.files]);

  if (!actualFiles.length) return <p className="text-center">{t('no-files-found')}</p>;

  const existingTypes = types.filter(({ checker }) => actualFiles.some(checker));

  const typeConfig = listGet(types, selectedType, 'value')!;

  const filteredFiles = actualFiles
    .filter(typeConfig.checker)
    .filter((f) => isSubstring(query, f.name) || isSubstring(query, f.filename));

  return (
    <Column>
      <Row className="h-9 w-full rounded-md border" gap="0">
        <DropdownMenu
          align="start"
          trigger={
            <Button variant="ghost" className="h-full">
              {t(typeConfig.value)} <ChevronDownIcon />
            </Button>
          }
        >
          <DropdownMenuTitle>{t('type')}</DropdownMenuTitle>

          {existingTypes.map((type) => (
            <DropdownMenuItem key={type.value} onClick={() => setState(type.value)}>
              {t(type.value)}
            </DropdownMenuItem>
          ))}
        </DropdownMenu>

        <Input
          className="h-full border-none pl-2"
          focusStyle="none"
          value={query}
          onChangeDirect={setQuery}
        />
      </Row>

      {filteredFiles.map((file) => (
        <Card key={file.id} className="grid grid-cols-12 items-center gap-4 text-foreground/50">
          <Row gap="2" className="col-span-9 grow text-primary">
            {isImageFile(file) ? (
              <PrivateImage url={file.url} className="h-20 max-w-40 border" />
            ) : (
              <>
                <FileUpIcon />
                <TruncateText text={fixFileName(file.name)} max={45} className="font-medium" />
              </>
            )}
          </Row>

          <Row
            gap="2"
            justifyContent="flex-end"
            className={`col-span-3 ${isImageFile(file) ? 'self-start' : ''}`}
          >
            {canUpdateTaskFile({
              task,
              project,
              user,
              file,
            }) && (
              <>
                <RenameTaskFileButton file={file} />

                <NewTaskFileVersionButton file={file} />
              </>
            )}

            <DownloadFileButton size={14} {...file} />

            <FileVersionTag file={file} />
          </Row>

          <Row gap="2" className="col-span-6">
            <UserAvatar
              user={file.uploadedBy}
              className="text-xs phone:!h-8 phone:!w-8"
              size={20}
            />

            <span className="text-sm font-medium text-foreground">
              {getFirstAndLastName(file.uploadedBy.name)}
            </span>
          </Row>

          <Tooltip content={formatPublishedAtDate(file.uploadedAt)}>
            <span className="col-span-6 text-end text-xs font-medium">
              {dateDistanceToNow(file.updatedAt || file.uploadedAt)}
            </span>
          </Tooltip>
        </Card>
      ))}
    </Column>
  );
}
